import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {createPortal} from 'react-dom';
import ModalContext from './ModalContext';
import {ModalFactory, ModalInstance, ModalOptions, ModalProviderConfig, ModalProviderProps} from './types';
import Modal from './Modal';

const ESC_KEY = 'Escape';

const defaultConfig: ModalProviderConfig = {
  appId: 'root',
  modalContainerId: 'modalContainer',
  bodyOpenClass: 'modal-open',
  modalContainerClass: 'modals',
  modalClass: 'modal',
};

let bodyOverflow: string = '';
let bodyMarginRight: string = '';

function ModalProvider({children, config = {}}: ModalProviderProps): JSX.Element {
  const [modalList, setModalList] = useState<Array<ModalInstance>>([]);
  const userConfig: ModalProviderConfig = {...defaultConfig, ...config};

  const modalSetup = useCallback(() => {
    document.getElementById(userConfig.appId)?.setAttribute('aria-hidden', 'true');
    bodyMarginRight = document.body.style.marginRight;
    bodyOverflow = document.body.style.overflow;
    document.body.style.marginRight = `${window.innerWidth - document.documentElement.clientWidth}px`;
    document.body.style.overflow = 'hidden';
    // TODO IOS 이슈 가능성 있음. 추후 이슈 발생 시 수정요망
    document.body.classList.add(userConfig.bodyOpenClass);
  }, [userConfig.appId, userConfig.bodyOpenClass]);

  const modalTakeDown = useCallback(() => {
    document.getElementById(userConfig.appId)?.removeAttribute('aria-hidden');
    document.body.style.marginRight = bodyMarginRight;
    document.body.style.overflow = bodyOverflow;
    document.body.classList.remove(userConfig.bodyOpenClass);
    bodyMarginRight = '';
    bodyOverflow = '';
  }, [userConfig.appId, userConfig.bodyOpenClass]);

  const closeModal = useCallback(
    (modalId: string, isForce: boolean = false) => {
      setModalList((list) => {
        if (list.length === 1) {
          modalTakeDown();
        }

        if (isForce) {
          const targetModal = list.find((modal) => modal.id === modalId);
          if (targetModal) {
            targetModal.forceClose();
          }
        }

        return list.filter((modal) => modal.id !== modalId);
      });
    },
    [modalTakeDown]
  );

  const closeAllModal = useCallback(
    (isForce: boolean = false) => {
      setModalList((list) => {
        if (isForce) {
          list.forEach((modal) => {
            modal.forceClose();
          });
        }
        return [];
      });
      modalTakeDown();
    },
    [modalTakeDown]
  );

  const openModal = useCallback(
    <R,>(modalFactory: ModalFactory<R>, modalOptions: ModalOptions<R> = {}): Promise<R | false> => {
      return new Promise((resolve) => {
        const id = getId();
        setModalList((list) => {
          if (list.length === 0) {
            modalSetup();
          }

          const ModalNode = modalFactory((result: R) => {
            closeModal(id);
            resolve(result);
          }, id);
          return [
            ...list,
            {
              ModalNode,
              id,
              forceClose() {
                resolve(false);
              },
              onEsc() {
                if (modalOptions.onEsc) {
                  closeModal(id);
                  resolve(modalOptions.onEsc());
                }
              },
            },
          ];
        });
      });
    },
    [closeModal, modalSetup]
  );

  useEffect(() => {
    function handleKey(event: KeyboardEvent) {
      if (modalList.length === 0) {
        return;
      }

      if (event.key === ESC_KEY) {
        const topModal = modalList[modalList.length - 1];
        topModal.onEsc();
      }
    }

    document.addEventListener('keydown', handleKey);

    return () => {
      document.removeEventListener('keydown', handleKey);
    };
  });

  const contextValue = useMemo(
    () => ({
      openModal,
      closeModal,
      closeAllModal,
    }),
    [openModal, closeModal, closeAllModal]
  );

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
      {createPortal(
        <div
          id={userConfig.modalContainerId}
          className={userConfig.modalContainerClass}
        >
          {modalList.map(({ModalNode, id}) => {
            return (
              <Modal
                key={id}
                modalClass={userConfig.modalClass}
              >
                {ModalNode}
              </Modal>
            );
          })}
        </div>,
        document.body
      )}
    </ModalContext.Provider>
  );
}

export default ModalProvider;

function getId() {
  return `Modal_${Math.floor(Math.random() * 10000)}`;
}
